/**
 * Things a visitor presses.
 *
 * ## One primary per screen
 *
 * `Action variant="primary"` is THE call to action: the accent gradient, the brand-tinted glow, the
 * lift on hover and the derived ink (`--a-on-primary`). It is the same object on the product page,
 * in the cart, in the checkout and on the subscribe form, because a buyer crosses those pages
 * mid-decision and a different button on the other side of a click reads as somebody else's site.
 * Beside it, the second action is `ghost` — an outline — never a second filled button: two primaries
 * side by side are none.
 *
 * ## A navigation is an anchor
 *
 * Every `Action`, `Link`, `Card` and `LinkTile` with an `href` or a `to` renders an anchor (see
 * `Pressable`); `onClick` on one of those is for reporting, and never stands between a visitor and
 * the page they asked for.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { cx, mod } from './cx';
import { Icon, type IconName } from './media';
import { Pressable } from './Pressable';

interface Target {
  /** Another origin, a `mailto:`, a legal page. */
  href?: string | undefined;
  /** A path inside this app, through its router (see `Anubis`). */
  to?: string | undefined;
  /** Open in a new tab. */
  external?: boolean | undefined;
  /** Reporting only for a link; the press itself for a button. */
  onClick?: (() => void) | undefined;
}

/**
 * A button, or a link that looks like one.
 *
 * `busy` disables it while a request is in flight — a second press on a payment or a confirmation is
 * how an order is placed twice — and the caller changes the words ("Placing your order…") rather than
 * anubis drawing a spinner over them.
 */
export function Action({
  children,
  variant = 'primary',
  size = 'md',
  block,
  type,
  disabled,
  busy,
  pressed,
  label,
  hidden,
  icon,
  ...target
}: Target & {
  children?: ReactNode | undefined;
  /** A glyph before the word — a sign-in network's mark. */
  icon?: IconName | undefined;
  variant?: 'primary' | 'ghost' | 'quiet' | undefined;
  size?: 'sm' | 'md' | 'lg' | undefined;
  /** Full width — the shape a phone wants, and the one a price box and a form use. */
  block?: boolean | undefined;
  type?: 'button' | 'submit' | undefined;
  disabled?: boolean | undefined;
  busy?: boolean | undefined;
  pressed?: boolean | undefined;
  label?: string | undefined;
  /** Out of the tab order and the accessibility tree — a control inside a `Dock` that is away. */
  hidden?: boolean | undefined;
}) {
  return (
    <Pressable
      {...target}
      type={type}
      disabled={disabled || busy}
      pressed={pressed}
      label={label}
      hidden={hidden}
      className={cx(
        'a-action',
        mod('a-action', 'variant', variant),
        mod('a-action', 'size', size),
        block && 'a-action--block',
        busy && 'a-action--busy',
      )}
    >
      {icon ? <Icon name={icon} size="sm" /> : null}
      {children}
    </Pressable>
  );
}

/** A link inside a sentence, or standing alone in a footer. */
export function Link({
  children,
  tone = 'accent',
  underline = 'hover',
  label,
  ...target
}: Target & {
  children?: ReactNode | undefined;
  tone?: 'accent' | 'accent-2' | 'inherit' | 'muted' | undefined;
  underline?: 'always' | 'hover' | 'none' | undefined;
  label?: string | undefined;
}) {
  return (
    <Pressable
      {...target}
      label={label}
      className={cx('a-link', mod('a-tone', 'is', tone), mod('a-link', 'underline', underline))}
    >
      {children}
    </Pressable>
  );
}

/** An email address as a `mailto:` link — the brand's support address, wherever it is printed. */
export function MailLink({
  email,
  tone = 'inherit',
}: {
  email: string;
  tone?: 'accent' | 'inherit' | undefined;
}) {
  return (
    <Link href={`mailto:${email}`} tone={tone} underline="always">
      {email}
    </Link>
  );
}

/**
 * The way to somebody who can help: a quiet outline with a question mark, linking to `help` (a new
 * case, prefilled from where the visitor stands — the receipt they were reading). It replaced the
 * support address printed in a footer (2026-10-20): a conversation that remembers the order beats
 * an email that has to describe it. `label` is the one word in the page's language ("Help",
 * "Ayuda"); anubis carries no words of its own.
 */
export function HelpButton({
  href,
  label,
  external,
  onClick,
}: {
  href: string;
  label: string;
  external?: boolean | undefined;
  onClick?: (() => void) | undefined;
}) {
  return (
    <Action href={href} external={external} onClick={onClick} variant="ghost" size="sm" icon="help">
      {label}
    </Action>
  );
}

/**
 * One choice out of a few, all visible: the language, a payment provider.
 *
 * Not a `<select>`: with two or three options a segmented control reads as "this page is in EN,
 * press ES" at a glance, which is what somebody who landed in the wrong one needs. `pill` is the
 * joined capsule for chrome in a corner, `text` is a pair of quiet words under a card, `tiles` is a
 * row of separate choices in a form.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  variant = 'pill',
}: {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
  /** The group's accessible name — "Language", "Pay with". */
  label: string;
  variant?: 'pill' | 'text' | 'tiles' | undefined;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cx('a-segmented', mod('a-segmented', 'variant', variant))}
    >
      {options.map((option) => (
        <Pressable
          key={option.value}
          pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className="a-segmented__option"
        >
          {option.label}
        </Pressable>
      ))}
    </div>
  );
}

/**
 * A few choices drawn as faces — glyphs, a number — pressed on and off: a filter's options, a
 * form's category (2026-10-20, Wall). `multiple` lets several be on at once; otherwise it is one or
 * none, and pressing the one that is on turns it off. `label` names the group and each option's
 * `label` names it, because its face may be only glyphs. `value` comes back in the options' order.
 * `shown` also writes `label` small on the left, in a column every shown `Picks` shares, so a form's
 * rows line up (2026-10-20, Wall's game form).
 */
export function Picks<T extends string>({
  options,
  value,
  onChange,
  label,
  multiple = false,
  shown = false,
}: {
  options: ReadonlyArray<{ value: T; label: string; face?: ReactNode }>;
  value: readonly T[];
  onChange: (next: T[]) => void;
  label: string;
  multiple?: boolean | undefined;
  shown?: boolean | undefined;
}) {
  const press = (picked: T) => {
    const on = value.includes(picked);
    if (!multiple) {
      onChange(on ? [] : [picked]);
      return;
    }
    const next = on ? value.filter((each) => each !== picked) : [...value, picked];
    onChange(options.map((option) => option.value).filter((each) => next.includes(each)));
  };
  const picks = (
    <div role="group" aria-label={label} className="a-picks">
      {options.map((option) => (
        <Pressable
          key={option.value}
          pressed={value.includes(option.value)}
          label={option.face ? option.label : undefined}
          onClick={() => press(option.value)}
          className="a-picks__option"
        >
          {option.face ?? option.label}
        </Pressable>
      ))}
    </div>
  );
  if (!shown) return picks;
  return (
    <div className="a-picks-row">
      <span className="a-picks-row__label" aria-hidden="true">
        {label}
      </span>
      {picks}
    </div>
  );
}

/**
 * A filter as a chip (2026-10-21, Wall — owner: *"the dropdowns appear below next to each other
 * like the chips in the details page"*): a pill naming the category — and, once something is
 * picked, the picked faces instead of the name — that drops a short list of the options under it.
 * Options are pressed on and off (`multiple`, or one or none) and the list STAYS open while they are,
 * so two or three are picked in one go; a press outside, Escape or the chip again closes it. Sits
 * in a row with its siblings (`Finder tools`).
 */
export function PickMenu<T extends string>({
  options,
  value,
  onChange,
  label,
  multiple = false,
}: {
  options: ReadonlyArray<{ value: T; label: string; face?: ReactNode }>;
  value: readonly T[];
  onChange: (next: T[]) => void;
  label: string;
  multiple?: boolean | undefined;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    window.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      window.removeEventListener('keydown', key);
    };
  }, [open]);
  const press = (picked: T) => {
    const on = value.includes(picked);
    if (!multiple) {
      onChange(on ? [] : [picked]);
      return;
    }
    const next = on ? value.filter((each) => each !== picked) : [...value, picked];
    onChange(options.map((option) => option.value).filter((each) => next.includes(each)));
  };
  const picked = options.filter((option) => value.includes(option.value));
  return (
    <div className="a-pickmenu" ref={root}>
      <button
        type="button"
        className={cx('a-pickmenu__chip', picked.length > 0 && 'a-pickmenu__chip--on')}
        aria-expanded={open}
        aria-controls={listId}
        aria-label={
          picked.length ? `${label}: ${picked.map((each) => each.label).join(', ')}` : label
        }
        onClick={() => setOpen((was) => !was)}
      >
        {picked.length ? (
          <span className="a-pickmenu__picked" aria-hidden="true">
            {picked.map((each) => (
              <span key={each.value} className="a-pickmenu__face">
                {each.face ?? each.label}
              </span>
            ))}
          </span>
        ) : (
          <span>{label}</span>
        )}
        <Icon name="chevron" size="sm" />
      </button>
      <div id={listId} role="group" aria-label={label} className="a-pickmenu__list" hidden={!open}>
        {options.map((option) => (
          <Pressable
            key={option.value}
            pressed={value.includes(option.value)}
            onClick={() => press(option.value)}
            className="a-pickmenu__option"
          >
            <span className="a-pickmenu__face" aria-hidden="true">
              {option.face ?? null}
            </span>
            <span className="a-pickmenu__label">{option.label}</span>
            {value.includes(option.value) ? <Icon name="check" size="sm" tone="accent" /> : null}
          </Pressable>
        ))}
      </div>
    </div>
  );
}

/**
 * A round control with a drawn glyph and, optionally, a count — the cart in a shop's bar.
 * `label` is required: an icon is not a name.
 */
export function IconAction({
  icon,
  label,
  count,
  ...target
}: Target & { icon: ReactNode; label: string; count?: number }) {
  return (
    <Pressable {...target} label={label} className="a-icon-action">
      {icon}
      {count ? <span className="a-icon-action__count">{count}</span> : null}
    </Pressable>
  );
}

/**
 * A whole tile that is one link — a product card. Lifts on hover, and a `Cover` inside it zooms in
 * its frame: two movements, one gesture. The tile SAYS it opens (put a `More` in it), because a
 * visitor cannot be relied on to discover that a picture is a door.
 */
export function Card({
  children,
  label,
  ...target
}: Target & { children?: ReactNode; label?: string }) {
  return (
    <Pressable {...target} label={label} className="a-card">
      {children}
    </Pressable>
  );
}

/** The quiet "View →" at the foot of a `Card`: muted until the card is hovered, then it moves. */
export function More({ children }: { children?: ReactNode }) {
  return (
    <span className="a-more">
      {children}
      <span aria-hidden="true" className="a-more__arrow">
        →
      </span>
    </span>
  );
}

/**
 * One full-width button on a link page: an icon at the start in the accent, the title centred, a ring
 * of the accent on hover drawn OVER the hairline so the tile does not move by a pixel.
 *
 * `tracking` is written as `data-link-label`, with `data-track-link`, which is what `links`' document
 * listener reports an outbound click by.
 */
export function LinkTile({
  href,
  icon,
  children,
  tracking,
}: {
  href: string;
  icon?: ReactNode | undefined;
  children?: ReactNode | undefined;
  tracking?: string | undefined;
}) {
  return (
    <Pressable
      href={href}
      external
      className="a-link-tile"
      data={tracking !== undefined ? { 'track-link': '', 'link-label': tracking } : undefined}
    >
      {icon ? (
        <span aria-hidden="true" className="a-link-tile__icon">
          {icon}
        </span>
      ) : null}
      <span className="a-link-tile__label">{children}</span>
    </Pressable>
  );
}
